// App.tsx
import React, { useState, useCallback, useEffect } from 'react';
import './styles/main.css';

import Sidebar, { PageKey } from './components/Sidebar';
import ProfilePicture from './components/ProfilePicture';
import ProfileModal from './components/ProfileModal';

import MainPage         from './pages/MainPage';
import TransactionsPage from './pages/TransactionsPage';
import AccountsPage     from './pages/AccountsPage';

import {UserProfile, fetchUserProfile} from './services/userService';
import { DEFAULT_USER_ID } from './helpers/HelperConstants';
import WelcomeModal, { shouldShowWelcome } from './components/WelcomeModal';
import { fetchAccounts } from './services/accountsService';

const App: React.FC = () => {

  const [currentPage, setCurrentPage]           = useState<PageKey>('home');
  const [sidebarOpen, setSidebarOpen]           = useState(false);
  
  // Profile
  const [loadingProfile, setLoadingProfile]     = useState(false);
  const [profile, setProfile]                   = useState<UserProfile | null>(null);
  const [profileModalOpen, setProfileModalOpen] = useState(false);

  // Welcome Modal
  const [welcomeOpen, setWelcomeOpen]           = useState(() => shouldShowWelcome());

  // Accounts Loading
  const [accountsMap, setAccountsMap]           = useState<Record<string,string>>({});
  const [accountsLoading, setAccountsLoading]   = useState(false);

  const loadAccounts = useCallback(()=> {
    setAccountsLoading(true);
    fetchAccounts(DEFAULT_USER_ID)
    .then((fetchedAccounts) => {
      let newMap: Record<string,string> = {}
      fetchedAccounts.forEach((a) => {
          newMap[a.accountId] = a.accountName
        })
      setAccountsMap(newMap);
    })
    .catch((err) => console.error("Failed to load accounts: ", err))
    .finally(()=> setAccountsLoading(false));
  }, []);

  useEffect(() => {
    setLoadingProfile(true);

    fetchUserProfile(DEFAULT_USER_ID)
      .then((fetched) => {
        const newProfile: UserProfile = {
          id: fetched.id,
          name: fetched.name,
          email: fetched.email,
          profilePictureUrl: fetched.profilePictureUrl
        };
        setProfile(newProfile);
      })
      .catch((err) => console.error("Failed to load user profile: ", err))
      .finally(() => setLoadingProfile(false));
    
    loadAccounts();
  }, [loadAccounts]);

  // Stable references — these never change identity between renders,
  // so child components that receive them as props won't remount.
  const handleLogout    = useCallback(() => { alert('Logged out'); }, []);
  const handleSeeMore   = useCallback(() => setCurrentPage('transactions'), []);
  const handleBack      = useCallback(() => setCurrentPage('home'), []);
  const handleNavigate  = useCallback((page: PageKey) => setCurrentPage(page), []);
  const handleClose     = useCallback(() => setSidebarOpen(false), []);
  const handleHamburger = useCallback(() => setSidebarOpen((prev) => !prev), []);

  const handleOpenProfile = useCallback(() => setProfileModalOpen(true), []);
  const handleCloseProfile = useCallback(() => setProfileModalOpen(false), []);
  const handleProfileUpdated = useCallback((updated: UserProfile) => {
    setProfile(updated);
  }, []);

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <MainPage accountsMap={accountsMap} onSeeMore={handleSeeMore} />;
      case 'transactions':
        return <TransactionsPage accountsMap={accountsMap} onBack={handleBack} />;
      case 'accounts':
        return <AccountsPage accountsMap={accountsMap} onAccountsChanged={loadAccounts}/>;
      default:
        return null;
    }
  };

  if (loadingProfile || accountsLoading) {
    return (
      <div className="app-loading">
        <div className="app-loading-inner">
          <div className="spinner" />
          <p className="app-loading-text">Loading application...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <Sidebar
        isOpen={sidebarOpen}
        currentPage={currentPage}
        onNavigate={handleNavigate}
        onClose={handleClose}
        onLogout={handleLogout}
      />

      <div className="main-content">
        <div className="topbar">
          <button
            className="hamburger-btn"
            onClick={handleHamburger}
            aria-label="Open navigation menu"
          >
            <span />
            <span />
            <span />
          </button>

          <ProfilePicture 
            src={profile?.profilePictureUrl}
            onClick={handleOpenProfile}
          />
        </div>

        {/* Welcome modal - shown on first visit */}
        {welcomeOpen && (<WelcomeModal onClose={() => setWelcomeOpen(false)}/>)}

        {renderPage()}
      </div>
      {/* Profile modal - rendered at app level so it overlays everything */}
      {profileModalOpen && (
        <ProfileModal
          userId={DEFAULT_USER_ID}
          profile={profile}
          onClose={handleCloseProfile}
          onProfileUpdated={handleProfileUpdated}
        />
      )}
    </div>
  );
};

export default App;